// ErrorHandler.jsx - A reusable component for handling errors
import React from 'react';
import './ErrorHandler.css';

const ErrorHandler = ({ 
  error, 
  resetError, 
  retryAction,
  isOffline = false
}) => {
  if (!error && !isOffline) return null;

  // Determine if this is a network error
  const isNetworkError = isOffline || 
    error?.message?.includes('network') || 
    error?.message?.includes('Network') ||
    error?.message?.includes('internet') ||
    error?.message?.includes('offline') ||
    error?.message?.includes('connection') ||
    error?.code === 'failed-precondition' || // Firestore offline error
    error?.code === 'unavailable'; // Firestore unavailable error

  // Determine if this is a server error
  const isServerError = error?.code === 'internal' || 
    error?.message?.includes('500') ||
    error?.message?.includes('server');

  // Determine if this is a permission error
  const isPermissionError = error?.code === 'permission-denied' ||
    error?.message?.includes('permission') ||
    error?.message?.includes('403');

  // Determine error type and message
  let errorTitle = 'Something went wrong';
  let errorMessage = error?.message || 'An unexpected error occurred. Please try again.';
  let iconType = 'general';

  if (isOffline || isNetworkError) {
    errorTitle = 'Network connection problem';
    errorMessage = 'It seems you are offline. Please check your internet connection and try again.';
    iconType = 'network';
  } else if (isServerError) {
    errorTitle = 'Server problem';
    errorMessage = 'We\'re having trouble connecting to our servers. Please try again later.';
    iconType = 'server';
  } else if (isPermissionError) {
    errorTitle = 'Access denied';
    errorMessage = 'You don\'t have permission to perform this action.';
    iconType = 'permission';
  }

  return (
    <div className="error-handler">
      <div className="error-container">
        <div className={`error-icon ${iconType}-icon`}>
          {iconType === 'network' && (
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path fill="currentColor" d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM19 18H6c-2.21 0-4-1.79-4-4 0-2.05 1.53-3.76 3.56-3.97l1.07-.11.5-.95A5.469 5.469 0 0112 6c2.62 0 4.88 1.86 5.39 4.43l.3 1.5 1.53.11A2.98 2.98 0 0122 15c0 1.65-1.35 3-3 3zm-9-3.82l-2.09-2.09L6.5 13.5 10 17l6.01-6.01-1.41-1.41-4.6 4.6z"/>
            </svg>
          )}
          {iconType === 'server' && (
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path fill="currentColor" d="M2 20h20v-4H2v4zm2-3h2v2H4v-2zM2 4v4h20V4H2zm4 3H4V5h2v2zm-4 7h20v-4H2v4zm2-3h2v2H4v-2z"/>
            </svg>
          )}
          {iconType === 'permission' && (
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path fill="currentColor" d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
            </svg>
          )}
          {iconType === 'general' && (
            <svg viewBox="0 0 24 24" width="36" height="36">
              <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          )}
        </div>
        <div className="error-content">
          <h3 className="error-title">{errorTitle}</h3>
          <p className="error-message">{errorMessage}</p>
          
          <div className="error-actions">
            {retryAction && (
              <button 
                className="retry-button"
                onClick={retryAction}
              >
                Try Again
              </button>
            )}
            {resetError && (
              <button 
                className="dismiss-button"
                onClick={resetError}
              >
                Dismiss
              </button>
            )}
          </div>

          {(isOffline || isNetworkError) && (
            <div className="error-help-text">
              <p>Troubleshooting tips:</p>
              <ul>
                <li>Check your Wi-Fi connection or mobile data</li>
                <li>Make sure airplane mode is turned off</li>
                <li>Check if other apps or websites work</li>
                <li>Try refreshing the page</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorHandler;